// src/services/paymentService.js
// Payment API helpers — mirrors invoiceService patterns

import api from '../api/axios';

/**
 * Small helper to normalize list responses.
 *
 * Supports both:
 *  - [ ... ]                           // plain array
 *  - { items: [...], meta: {...} }     // paginated
 */
function normalizeListResponse(data, params = {}) {
  if (Array.isArray(data)) {
    const total = data.length;
    const limit = Number(params.limit ?? total ?? 25);
    const page = Number(params.page ?? 1);

    return {
      items: data,
      meta: {
        page,
        limit,
        total,
        totalPages: 1,
      },
    };
  }

  const items = Array.isArray(data?.items) ? data.items : [];
  const meta = data?.meta || {};

  return {
    items,
    meta,
  };
}

/* -----------------------------------------------------------------------------
 * Global payments list
 * -------------------------------------------------------------------------- */

/**
 * listPayments
 *
 * GET /payments
 *
 * Query params (all optional, you can support these in your controller):
 * - page, limit
 * - search        (paymentNumber, client name, invoiceNumber, reference)
 * - status        (pending/succeeded/failed/refunded/disputed/canceled)
 * - method        (card/cash/cheque/bank_transfer/e_transfer/other)
 * - source        (manual/stripe)
 * - dateField     (paymentDate/createdAt)
 * - dateFrom      (ISO date-only, e.g. "2025-11-18")
 * - dateTo        (ISO date-only)
 * - sortBy        (paymentDate, createdAt, amount, netAmount)
 * - sortOrder     ("asc" | "desc")
 */
export async function listPayments(params = {}) {
  const res = await api.get('/payments', { params });
  const data = res?.data ?? {};
  return normalizeListResponse(data, params);
}

/* -----------------------------------------------------------------------------
 * Single payment CRUD (for future use)
 * -------------------------------------------------------------------------- */

/**
 * fetchPayment
 *
 * GET /payments/:paymentId
 */
export async function fetchPayment(paymentId) {
  if (!paymentId) throw new Error('paymentId is required');
  const res = await api.get(`/payments/${paymentId}`);
  return res?.data ?? null;
}

/**
 * createInvoicePayment
 *
 * POST /invoices/:invoiceId/payments
 *
 * Used from invoice detail page to record a manual payment.
 * Payload example:
 * {
 *   paymentDate,
 *   amount,
 *   currency,
 *   method,
 *   source: 'manual',
 *   reference,
 *   notes
 * }
 */
export async function createInvoicePayment(invoiceId, payload) {
  if (!invoiceId) throw new Error('invoiceId is required');
  const res = await api.post(`/invoices/${invoiceId}/payments`, payload);
  return res?.data ?? null;
}

/**
 * updatePayment
 *
 * PATCH /payments/:paymentId
 */
export async function updatePayment(paymentId, payload) {
  if (!paymentId) throw new Error('paymentId is required');
  const res = await api.patch(`/payments/${paymentId}`, payload);
  return res?.data ?? null;
}

/**
 * deletePayment
 *
 * DELETE /payments/:paymentId
 *
 * NOTE: Not wired into the UI yet — in Payments.jsx the destructive
 * call is commented out on purpose. When your backend supports it,
 * you can:
 *  - uncomment the import in Payments.jsx
 *  - uncomment the call in doDeletePayment
 */
export async function deletePayment(paymentId) {
  if (!paymentId) throw new Error('paymentId is required');
  const res = await api.delete(`/payments/${paymentId}`);
  return res?.data ?? null;
}
